import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface PasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userName?: string;
}

export function PasswordDialog({ open, onOpenChange, userId, userName }: PasswordDialogProps) {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    if (!password || password.length < 6) {
        toast.error(t('admin.users.passwordDialog.minLength'));
        return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.functions.invoke('admin-users', {
        body: { action: 'update_password', userId, password }
      });

      if (error) throw error;

      toast.success(t('admin.users.passwordDialog.success'));
      onOpenChange(false);
      setPassword("");
    } catch (error) {
      console.error("Update failed:", error);
      const message = error instanceof Error ? error.message : 'Unknown error';
      toast.error(t('admin.users.passwordDialog.failed', { error: message }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md border-2 border-black bg-[#F5F0E8] p-0 shadow-[4px_4px_0_#000]">
        <div className="p-6">
          <DialogHeader className="mb-6">
            <DialogTitle className="text-2xl font-black text-[#1A1A1A]">
              {t('admin.users.passwordDialog.title')}
            </DialogTitle>
            <DialogDescription className="mt-2 text-sm font-medium text-gray-500">
              {t('admin.users.passwordDialog.desc', { name: userName || t('admin.users.passwordDialog.user') })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
               <label className="mb-2 block text-sm font-bold text-gray-600">{t('admin.users.passwordDialog.label')}</label>
               <input 
                 type="password"
                 value={password}
                 onChange={(e) => setPassword(e.target.value)}
                 className="w-full px-4 py-3 bg-white border-2 border-black rounded-lg focus:outline-none focus:ring-2 focus:ring-black font-medium"
                 placeholder={t('admin.users.passwordDialog.placeholder')}
               />
            </div>

            {/* Tip Box */}
            <div className="rounded-lg border-2 border-black bg-[#FFD93D] p-4">
               <p className="text-sm font-bold leading-relaxed text-[#1A1A1A]">
                 💡 {t('admin.users.passwordDialog.tip')}
               </p>
            </div>
          </div>

          <DialogFooter className="mt-8 flex items-center gap-3">
             {/* Cancel */}
            <button 
              onClick={() => onOpenChange(false)}
              className="flex-1 px-6 py-2 bg-white border-2 border-black rounded-full hover:bg-gray-100 font-bold transition-all text-sm"
            >
              {t('admin.users.passwordDialog.cancel')}
            </button>

            {/* Confirm */}
            <button 
              onClick={handleSave}
              disabled={loading}
              className="flex-1 px-6 py-2 bg-[#6FEDD6] border-2 border-black rounded-full hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0_#1A1A1A] transition-all font-bold flex items-center justify-center gap-2 text-sm disabled:opacity-50"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('admin.users.passwordDialog.confirm')}
            </button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
